import { GridColumnProps } from '@progress/kendo-react-grid';
import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import styled from 'styled-components';
import React, { useEffect, useRef, useMemo } from 'react';
import { GridRowProps } from '@progress/kendo-react-grid';
import { SHToolTip } from 'app/components/SHToolTip';

type Props = {
    data: any[] | undefined;
    columns?: GridColumnProps[] | undefined;
    divisionName?: string;
    isMultiDivision?: boolean;
    allShows?: any[];
};

const InnerViewTable = ({ data, columns, divisionName, isMultiDivision = false, allShows = [] }: Props) => {
    const gridWrapperRef = useRef<HTMLDivElement>(null);

    // For single division, add division row as first row
    const dataWithDivision = useMemo(() => {
        if (!data) return data;

        if (isMultiDivision) {
            return data;
        }

        if (!divisionName) return data;

        const divisionRow: any = {};
        if (columns && columns.length > 0) {
            divisionRow[columns[0].field as string] = divisionName;
            divisionRow._isDivisionRow = true;
            divisionRow._divisionName = divisionName;
        }
        return [divisionRow, ...data];
    }, [data, divisionName, columns, isMultiDivision]);

    // Header cell renderer
    const HeaderCell = (props: any, showInfo?: any) => {
        const { title, field } = props;

        // Render show columns with date
        if (showInfo) {
            return (
                <SHToolTip position="top">
                    <TableHeaderWrapper style={{ textAlign: 'center' }}>
                        <div
                            className="title"
                            title={title}
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                width: '100%',
                                flex: 1,
                                lineHeight: '18px',
                                textAlign: 'center',
                            }}
                        >
                            {title}
                        </div>
                        <div className="date" style={{ textAlign: 'center' }}>{showInfo.date}</div>
                    </TableHeaderWrapper>
                </SHToolTip>
            );
        }

        // Regular column headers
        let headerTitle;
        let isPointsTotal = false;
        switch (field) {
            case 'totalRides':
                headerTitle = 'Tot Rides To Date';
                break;
            case 'firstName':
                headerTitle = 'First Name';
                break;
            case 'lastName':
                headerTitle = 'Last Name';
                break;
            case 'backNumber':
                headerTitle = 'Back#';
                break;
            case 'pointsTotal':
                headerTitle = 'Points Total';
                isPointsTotal = true;
                break;
            default:
                headerTitle = title;
        }

        return (
            <StaticTableHeaderWrapper style={{ textAlign: isPointsTotal ? "center" : "left" }}>
                <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isPointsTotal ? '#4caf50' : '#475467',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    width: '100%',
                    flex: 1,
                    lineHeight: '22px'
                }}>
                    {headerTitle}
                </div>
            </StaticTableHeaderWrapper>
        );
    };

    // Data cell renderer
    const DataCell = (props: any, field: string, isPointsTotal: boolean = false) => {
        const { dataItem, className, style } = props;
        const cellData = dataItem[field];

        // Handle division rows
        if (dataItem._isDivisionRow) {
            return null;
        }

        // Handle object data (show results)
        if (typeof cellData === 'object' && cellData !== null && cellData?.value !== undefined) {
            return (
                <TableDataWrapper style={{ textAlign: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        {cellData.value}
                    </div>
                </TableDataWrapper>
            );
        }

        // Handle numeric values that should be centered
        const isCenterAligned = ['totalRides', 'backNumber', 'pointsTotal'].includes(field);

        return (
            <td
                className={className}
                style={{
                    ...style,
                    textAlign: isCenterAligned ? 'center' : 'inherit',
                    ...(isPointsTotal && {
                        color: '#4caf50',
                        fontWeight: '600'
                    })
                }}
            >
                {cellData}
            </td>
        );
    };

    // Update columns with custom cell renderers and headers
    const updatedColumns = useMemo(() => {
        if (!columns) return columns;

        return columns?.map((col) => {
            const showInfo = allShows?.find(show => show.name === col.field);
            const isPointsTotal = col.field === 'pointsTotal';
            const isShowColumn = !!showInfo;

            return {
                ...col,
                headerCell: (props: any) => HeaderCell(props, isShowColumn ? showInfo : undefined),
                cell: (props: any) => {
                    // Special handling for division rows
                    if (props.dataItem._isDivisionRow) {
                        if (col.field === 'totalRides') {
                            return (
                                <DivisionNameCell
                                    colSpan={columns?.length}
                                    style={{ width: '100%' }}
                                    className='DivisionNameCell'
                                >
                                    {props.dataItem._divisionName}
                                </DivisionNameCell>
                            );
                        }
                        return null;
                    }

                    // Regular data cell rendering
                    return DataCell(props, col.field as string, isPointsTotal);
                },
            };
        });
    }, [columns, allShows]);

    // Use rowRender to add division-row class for CSS styling
    const rowRender = (trElement: React.ReactElement, props: GridRowProps) => {
        if (props.dataItem._isDivisionRow) {
            return React.cloneElement(trElement, {
                className: `${trElement.props.className || ''} division-row`,
            });
        }
        return trElement;
    };

    useEffect(() => {
        const gridWrapper = gridWrapperRef.current;
        if (!gridWrapper) return;

        // Kendo Grid scroll sync
        const gridContent = gridWrapper.querySelector('.k-grid-content') as HTMLElement;
        const gridHeaderWrap = gridWrapper.querySelector('.k-grid-header-wrap') as HTMLElement;
        const topScrollbar = gridWrapper.querySelector('.top-scrollbar') as HTMLElement;
        const scrollbarInner = gridWrapper.querySelector('.scrollbar-inner') as HTMLElement;

        // Update scrollbar inner width to match grid content
        const updateScrollbarWidth = () => {
            if (gridContent && scrollbarInner) {
                const scrollWidth = gridContent.scrollWidth;
                scrollbarInner.style.width = `${scrollWidth}px`;
            }
        };

        // Initial update
        updateScrollbarWidth();

        const handleContentScroll = () => {
            if (gridHeaderWrap && gridContent) {
                gridHeaderWrap.scrollLeft = gridContent.scrollLeft;
            }
            if (topScrollbar && gridContent) {
                topScrollbar.scrollLeft = gridContent.scrollLeft;
            }
        };

        const handleHeaderScroll = () => {
            if (gridContent && gridHeaderWrap) {
                gridContent.scrollLeft = gridHeaderWrap.scrollLeft;
            }
            if (topScrollbar && gridHeaderWrap) {
                topScrollbar.scrollLeft = gridHeaderWrap.scrollLeft;
            }
        };

        const handleTopScrollbarScroll = () => {
            if (gridContent && topScrollbar) {
                gridContent.scrollLeft = topScrollbar.scrollLeft;
            }
            if (gridHeaderWrap && topScrollbar) {
                gridHeaderWrap.scrollLeft = topScrollbar.scrollLeft;
            }
        };

        if (gridContent) {
            gridContent.addEventListener('scroll', handleContentScroll);
        }

        if (gridHeaderWrap) {
            gridHeaderWrap.addEventListener('scroll', handleHeaderScroll);
        }

        if (topScrollbar) {
            topScrollbar.addEventListener('scroll', handleTopScrollbarScroll);
        }

        // Update width on window resize
        const handleResize = () => {
            updateScrollbarWidth();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            if (gridContent) {
                gridContent.removeEventListener('scroll', handleContentScroll);
            }
            if (gridHeaderWrap) {
                gridHeaderWrap.removeEventListener('scroll', handleHeaderScroll);
            }
            if (topScrollbar) {
                topScrollbar.removeEventListener('scroll', handleTopScrollbarScroll);
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [dataWithDivision, updatedColumns]);

    return (
        <>
            <CardWrapper style={{ paddingTop: '15px' }}>
                <CardSubWrapper className="d-flex"></CardSubWrapper>
                <GridWrapper ref={gridWrapperRef}>
                    <TopScrollbarContainer className="top-scrollbar">
                        <ScrollbarInner className="scrollbar-inner" />
                    </TopScrollbarContainer>
                    <CustomDataGrid
                        showPagination={false}
                        className="custom-data-grid"
                        data={dataWithDivision}
                        columns={updatedColumns}
                        rowRender={rowRender}
                    />
                </GridWrapper>
            </CardWrapper>
        </>
    );
};

export default InnerViewTable;

const DivisionNameCell = styled.td`
    background: #4caf50 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
    padding: 12px 8px !important;
    font-size: 14px !important;
    border: 1px solid #388e3c !important;
    white-space: normal !important;
    word-break: break-word !important;
    position: sticky !important;
    left: 0 !important;
    z-index: 11 !important;
    
    &:hover {
        background: #4caf50 !important;
        color: white !important;
    }
`;

const TableHeaderWrapper = styled.th`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-family: 'Inter';
    font-size: 14px;
    font-weight: 600 !important;
    line-height: 14.52px;
    color: #475467;
    margin: unset !important;
    min-height: 80px;
    padding: 8px 4px !important;
    gap: 8px;
    text-align: center;

    .title {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .date {
        font-size: 12px;
        font-weight: 400 !important;
        white-space: nowrap;
        text-align: center;
        width: 100%;
    }
`;

const StaticTableHeaderWrapper = styled.div`
    div {
        padding-left: 8px;
    }
`;

const TableDataWrapper = styled.td`
    width: 100% !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-align: center !important;
`;

const TopScrollbarContainer = styled.div`
    overflow-x: scroll;
    overflow-y: hidden;
    margin-bottom: 0;
    width: 100%;
    height: 35px;
    
    &::-webkit-scrollbar {
        height: 20px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 6px;
        
        &:hover {
            background: #555;
        }
    }
    
    /* Firefox scrollbar styling */
    scrollbar-color: #888 #f1f1f1;
    scrollbar-width: thick;
`;

const ScrollbarInner = styled.div`
    height: 1px;
    width: 100%;
    min-width: 100%;
`;

const GridWrapper = styled.div`
    margin-bottom: 10px;
    width: 100%;

    /* Style the entire division row */
    .k-grid-content tr.division-row {
        background: #4caf50 !important;
        border: 1px solid #388e3c !important;
        
        td {
            background: #4caf50 !important;
            color: white !important;
            border: 1px solid #388e3c !important;
            padding: 12px 8px !important;
        }
        
        &:hover {
            background: #4caf50 !important;
            td {
                background: #4caf50 !important;
                color: white !important;
            }
        }
    }

    /* Enable horizontal scroll on header wrap */
    .k-grid-header-wrap {
        overflow-x: scroll !important;
        overflow-y: hidden !important;
        
        &::-webkit-scrollbar {
            height: 0px;
        }
        
        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 3px;
        }
    }

    /* Style the grid content scrollbar to match top scrollbar */
    .k-grid-content {
        &::-webkit-scrollbar {
            height: 20px;
        }
        
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
        }

        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 6px;
            
            &:hover {
                background: #555;
            }
        }
        
        /* Firefox scrollbar styling */
        scrollbar-color: #888 #f1f1f1;
        scrollbar-width: thick;
    }

    .k-header {
        align-content: center;
    }
    
    .k-grid-content tr {
        &:hover {
            td {
                background: #f0f0f0 !important;
            }
        }
    }
    
    .custom-data-grid {
        th {
            vertical-align: baseline;
            padding: 5px !important;
        }
        .k-virtual-content {
            height: unset !important;
            min-height: unset;
        }
    }
    
    .k-grid-content tr {
        .k-grid-content-sticky {
            &:hover {
                background: #f0f0f0 !important;
            }
        }
    }
    
    .k-grid-content tr td.k-grid-content-sticky {
        cursor: unset;
    }
`;